import classnames from 'classnames';
import React, {useMemo} from 'react';

import {Text} from '@albert-io/atomic';
import {alphaComparatorAscending, numericComparatorDescending} from 'lib/SortUtil';
import {BUCKETS_ARRAY_INCREMENTING} from 'lib/StatsUtil';
import type {MasteryBucket} from 'lib/StatsUtil';
import type {StudentsListCollection} from 'resources/Student/StudentsListCollection.model';

import type {OverviewSortKey} from './OverviewSortMenu';
import {StudentCard} from './StudentCard';
import type {OverviewStudent} from './StudentCard';

// Each band's pill uses the semantic band utility (→ --color-data-* token), matching the Reports
// performance cells (see lib/StatsUtil COLOR).
const BUCKET_BG: Record<string, string> = {
  STRUGGLING: 'background-struggling',
  PASSING: 'background-passing',
  PROFICIENT: 'background-proficient',
  EXCELLING: 'background-excelling',
  MASTERY: 'background-mastery'
};

const studentsForBucket = (
  students: StudentsListCollection,
  bucket: MasteryBucket
): OverviewStudent[] => {
  switch (bucket.BUCKET_NAME) {
    case 'STRUGGLING':
      return students.getStrugglingStudents().toArray() as unknown as OverviewStudent[];
    case 'PASSING':
      return students.getPassingStudents().toArray() as unknown as OverviewStudent[];
    case 'PROFICIENT':
      return students.getProficientStudents().toArray() as unknown as OverviewStudent[];
    case 'EXCELLING':
      return students.getExcellingStudents().toArray() as unknown as OverviewStudent[];
    case 'MASTERY':
      return students.getMasteryStudents().toArray() as unknown as OverviewStudent[];
    default:
      return [];
  }
};

// Sort within a band, mirroring the legacy SortByBar (name A–Z; questions / accuracy high→low).
const sortStudents = (roster: OverviewStudent[], sortBy: OverviewSortKey): OverviewStudent[] => {
  const sorted = [...roster];
  if (sortBy === 'questions') {
    sorted.sort((a, b) =>
      numericComparatorDescending(a.getMeta().getCountOfGuesses(), b.getMeta().getCountOfGuesses())
    );
  } else if (sortBy === 'accuracy') {
    sorted.sort((a, b) =>
      numericComparatorDescending(a.getAccuracyFromMeta(), b.getAccuracyFromMeta())
    );
  } else {
    sorted.sort((a, b) => alphaComparatorAscending(a.getFullName(), b.getFullName()));
  }
  return sorted;
};

interface Props {
  students: StudentsListCollection;
  classroomId: string;
  sortBy: OverviewSortKey;
}

const MasteryColumnsBase = ({students, classroomId, sortBy}: Props) => {
  // precompute them instead of re-running filter+sort each time.
  const sortedBuckets = useMemo(
    () =>
      BUCKETS_ARRAY_INCREMENTING.map((bucket) => ({
        bucket,
        students: sortStudents(studentsForBucket(students, bucket), sortBy)
      })),
    [students, sortBy]
  );

  return (
    <div className='flex flex-col gap-x4'>
      {sortedBuckets.map(({bucket, students: bucketStudents}) => (
        <div
          key={bucket.BUCKET_NAME}
          role='group'
          aria-label={`${bucket.DISPLAY_NAME}, ${bucket.MIN}-${bucket.MAX}%`}
          className='flex flex-col gap-x2'
        >
          <span
            className={classnames(
              'self-start rounded-full px-x1-half py-x0-quarter content-primary-inverse-static',
              BUCKET_BG[bucket.BUCKET_NAME]
            )}
          >
            <Text size='s' bold color='inherit'>
              {bucket.MIN}-{bucket.MAX}%
            </Text>
          </span>
          {bucketStudents.length ? (
            <ul className='m-x0 grid list-none grid-cols-1 gap-x2 p-x0 s:grid-cols-2 m:grid-cols-3'>
              {bucketStudents.map((student) => (
                <li key={student.getId()}>
                  <StudentCard student={student} classroomId={classroomId} />
                </li>
              ))}
            </ul>
          ) : (
            <Text size='s' color='secondary' className='py-x2 text-center'>
              No students found
            </Text>
          )}
        </div>
      ))}
    </div>
  );
};

export const MasteryColumns = React.memo(MasteryColumnsBase);
